import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Spinner, SpinnerSize } from "@fluentui/react";
import { accessService } from "../../services/accessService";
import EntityDrawer from "./EntityDrawer";

// ─────────────────────────────────────────────────────────────────────────────
// Portal search — Cmd/Ctrl-K Spotlight-style overlay.
// Backed by GET /api/search. Frontend is presentational only; ranking,
// grouping, and access-control filtering all live on the server.
//
// This file exports two components:
//   <SearchTrigger onOpen={...} />   the pill button in TopNav
//   <SearchOverlay open onClose />   the full-screen modal
// The Ctrl+K listener is owned by the parent (TopNav) so the trigger and
// keyboard shortcut share one open/close source of truth.
// ─────────────────────────────────────────────────────────────────────────────

interface ResultItem {
  entityType: string;
  id: string;
  primary: string;
  secondary: string;
  meta: string;
  tier: "public" | "governed" | "pii";
  preview: {
    eyebrow: string;
    title: string;
    sub: string;
    rows: [string, string][];
  };
}
interface ResultGroup {
  type: string;
  entityType: string;
  count: number;
  items: ResultItem[];
}
interface SearchResponse {
  query: string;
  totalResults: number;
  scope: string[];
  groups: ResultGroup[];
}

const ENTITY_ICON: Record<string, string> = {
  cost_centre:     "Org",
  project:         "Suitcase",
  supplier:        "DeliveryTruck",
  purchase_order:  "ShoppingCart",
  property:        "Home",
  permit:          "PageList",
  infringement:    "Warning",
  asset:           "Build",
  service_request: "Chat",
  customer:        "Contact",
  dashboard:       "BarChart4",
  dataset:         "Database",
};

const RECENT_DEFAULT = [
  { label: "C10014 road reno",     query: "C10014" },
  { label: "Boral Resources",      query: "boral" },
  { label: "CC 51100 Engineering", query: "51100" },
  { label: "Finance dashboard",    query: "finance" },
];

// Entity types that open the Entity 360 drawer instead of routing to a page.
// Everything else (dashboards, datasets) routes to its dedicated nav surface.
// Permit / infringement are deferred — their warehouse views exist but have
// no rows on dev yet; they'll join the drawer set once data lands.
const DRAWER_ENTITY_TYPES = new Set([
  "cost_centre", "project", "supplier", "purchase_order",
  "property", "asset", "service_request", "customer",
]);

// Route to navigate to for items that don't have an Entity 360 drawer yet.
function routeFor(item: ResultItem): string | null {
  if (DRAWER_ENTITY_TYPES.has(item.entityType)) return null; // opens drawer instead
  switch (item.entityType) {
    case "dashboard": {
      const row = item.preview.rows.find(([k]) => k === "Route");
      return row ? row[1] : null;
    }
    case "dataset":
      return "/datasets";
    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Trigger pill — drops into the top nav between the link row and the right.
// ─────────────────────────────────────────────────────────────────────────────
export function SearchTrigger({ onOpen }: { onOpen: () => void }) {
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  return (
    <>
      <style>{`
        .portal-search-trigger {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 7px 8px 7px 14px;
          background: #FAFAFA;
          border: 1px solid #E1E1E1;
          border-radius: 6px;
          color: #747474;
          font-size: 13px;
          font-family: inherit;
          width: 100%;
          min-width: 0;
          transition: all 0.15s ease;
          cursor: pointer;
        }
        .portal-search-trigger:hover {
          border-color: #2e873d;
          background: #FFFFFF;
          color: #484848;
          box-shadow: 0 1px 3px rgba(46, 135, 61, 0.08);
        }
        .portal-search-trigger-text { flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .portal-search-kbd {
          font-family: "Cascadia Code", Consolas, "SF Mono", ui-monospace, monospace;
          font-size: 11px;
          color: #747474;
          background: #FFFFFF;
          border: 1px solid #E1E1E1;
          border-bottom-width: 2px;
          border-radius: 4px;
          padding: 1px 6px;
          line-height: 1.5;
        }
        @media (max-width: 768px) {
          .portal-search-trigger { display: none; }
        }
      `}</style>
      <button className="portal-search-trigger" onClick={onOpen} aria-label="Open portal search">
        <Icon iconName="Search" style={{ color: "#747474", fontSize: 13 }} />
        <span className="portal-search-trigger-text">Search projects, suppliers, cost centres…</span>
        <span className="portal-search-kbd">{isMac ? "⌘" : "Ctrl"}</span>
        <span className="portal-search-kbd">K</span>
      </button>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Overlay — full-screen modal with input + two-pane results.
// ─────────────────────────────────────────────────────────────────────────────
// Meta-search items are still in the catalog (so admins can grant them) but
// are not surfaced in the scope-tab row. They are accessed via the existing
// /dashboards and /datasets nav pages rather than via search.
const META_ENTITY_TYPES = new Set(["dashboard", "dataset"]);

export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState<ResultGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<string[]>([]);
  const [activeScope, setActiveScope] = useState<string>("all");
  const [selectedIdx, setSelectedIdx] = useState(0);
  // Drawer for the selected entity's full profile. Lives on top of the modal;
  // Esc closes the drawer first, then a second Esc closes the modal.
  const [drawerTarget, setDrawerTarget] = useState<{ entityType: string; id: string; title: string } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Flat list of items in render order — drives keyboard navigation
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const selected = flat[selectedIdx];

  // Granted entity types that get a scope tab (excludes meta-search items)
  const scopeTabs = useMemo(
    () => scope.filter((s) => !META_ENTITY_TYPES.has(s)),
    [scope]
  );

  // Focus input + reset selection on open
  useEffect(() => {
    if (open) {
      setSelectedIdx(0);
      setActiveScope("all");
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      setQ("");
      setGroups([]);
      setError(null);
    }
  }, [open]);

  // Fetch scope once so the empty state knows what entity types to advertise
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ items: string[] }>("/search/scope");
        if (alive) setScope(res.data.items || []);
      } catch { /* empty scope is fine */ }
    })();
    return () => { alive = false; };
  }, []);

  // Debounced search — re-runs on q or active scope change
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) {
      setGroups([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: term });
        if (activeScope && activeScope !== "all") params.set("entityType", activeScope);
        const res = await accessService.get<SearchResponse>(
          "/search?" + params.toString()
        );
        setGroups(res.data.groups || []);
        setSelectedIdx(0);
      } catch (e: any) {
        setError(e?.response?.data?.error || e.message || "Search failed");
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q, activeScope, open]);

  const confirm = useCallback(() => {
    const item = flat[selectedIdx];
    if (!item) return;
    if (DRAWER_ENTITY_TYPES.has(item.entityType)) {
      setDrawerTarget({ entityType: item.entityType, id: item.id, title: item.preview.title });
      return;
    }
    const r = routeFor(item);
    if (r) {
      onClose();
      nav(r);
    }
  }, [flat, selectedIdx, onClose, nav]);

  const closeDrawer = useCallback(() => setDrawerTarget(null), []);

  // Keyboard nav while open. Esc closes the drawer first if it's open, then
  // the modal. Arrow keys are ignored while drawer is open so they don't
  // shuffle results behind it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (drawerTarget) {
        if (e.key === "Escape") {
          e.preventDefault();
          closeDrawer();
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown" && flat.length) {
        e.preventDefault();
        setSelectedIdx((i) => (i + 1) % flat.length);
      } else if (e.key === "ArrowUp" && flat.length) {
        e.preventDefault();
        setSelectedIdx((i) => (i - 1 + flat.length) % flat.length);
      } else if (e.key === "Enter" && flat.length) {
        e.preventDefault();
        confirm();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, flat.length, confirm, onClose, drawerTarget, closeDrawer]);

  if (!open) return null;

  return (
    <>
      <style>{`
        .ps-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 19, 0.32);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          display: flex; align-items: flex-start; justify-content: center;
          padding-top: 96px;
          z-index: 1000;
          animation: ps-fade 0.16s ease;
        }
        @keyframes ps-fade { from { opacity: 0; } to { opacity: 1; } }
        .ps-modal {
          width: min(880px, calc(100vw - 32px));
          max-height: calc(100vh - 192px);
          background: #FFFFFF;
          border-radius: 12px;
          box-shadow: 0 24px 80px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.10);
          overflow: hidden;
          display: grid;
          grid-template-rows: auto 1fr auto;
          animation: ps-rise 0.22s cubic-bezier(.16,.84,.36,1);
        }
        @keyframes ps-rise { from { transform: translateY(-8px) scale(0.985); } to { transform: none; } }
        .ps-input-wrap {
          display: flex; align-items: center; gap: 12px;
          padding: 16px 20px;
          border-bottom: 1px solid #EDEDED;
          background: linear-gradient(to bottom, #FFFFFF, #FAFAFA);
        }
        .ps-input {
          flex: 1; border: none; outline: none;
          font-family: inherit; font-size: 16px; color: #232323;
          background: transparent; letter-spacing: -0.01em;
        }
        .ps-input::placeholder { color: #9aa0a6; }
        .ps-esc { display: flex; align-items: center; gap: 6px; color: #747474; font-size: 11px; }

        /* Scope tabs row — sits between input and results */
        .ps-scope {
          display: flex; align-items: center; gap: 4px;
          padding: 8px 16px;
          background: #FFFFFF;
          border-bottom: 1px solid #EDEDED;
          overflow-x: auto;
          scrollbar-width: thin;
        }
        .ps-scope::-webkit-scrollbar { height: 4px; }
        .ps-scope::-webkit-scrollbar-thumb { background: #DADCDF; border-radius: 2px; }
        .ps-scope-tab {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 600;
          font-family: inherit;
          color: #484848;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 14px;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.12s;
        }
        .ps-scope-tab:hover { background: #F5F5F5; color: #232323; }
        .ps-scope-tab.ps-scope-active {
          background: #d9f4d7;
          color: #2e873d;
          border-color: rgba(46,135,61,0.3);
        }
        .ps-scope-tab.ps-scope-active:hover { background: #d9f4d7; }
        .ps-scope-tab .ps-scope-pii {
          display: inline-block;
          font-size: 8.5px; font-weight: 700; letter-spacing: 0.06em;
          color: #B91C1C;
          background: rgba(185,28,28,0.10);
          padding: 1px 4px;
          border-radius: 2px;
        }
        .ps-scope-label {
          font-size: 11px;
          color: #747474;
          font-weight: 500;
          margin-right: 4px;
          white-space: nowrap;
        }
        .ps-kbd {
          font-family: "Cascadia Code", Consolas, "SF Mono", ui-monospace, monospace;
          font-size: 11px; color: #747474; background: #FFFFFF;
          border: 1px solid #E1E1E1; border-bottom-width: 2px;
          border-radius: 4px; padding: 1px 6px; line-height: 1.5;
        }
        .ps-body { display: grid; grid-template-columns: minmax(320px, 1fr) 320px; min-height: 0; }
        @media (max-width: 720px) {
          .ps-body { grid-template-columns: 1fr; }
          .ps-preview { display: none; }
        }
        .ps-results { overflow-y: auto; padding: 6px 0; background: #FFFFFF; border-right: 1px solid #EDEDED; }
        .ps-results::-webkit-scrollbar, .ps-preview::-webkit-scrollbar { width: 6px; }
        .ps-results::-webkit-scrollbar-thumb, .ps-preview::-webkit-scrollbar-thumb { background: #DADCDF; border-radius: 3px; }
        .ps-group-header {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 20px 6px;
          font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
          color: #747474;
        }
        .ps-group-header .ps-count { color: #9aa0a6; font-weight: 500; }
        .ps-result {
          display: grid; grid-template-columns: 32px 1fr auto;
          align-items: center; column-gap: 12px;
          padding: 10px 20px;
          border-left: 3px solid transparent;
          cursor: pointer; transition: background 0.1s;
        }
        .ps-result:hover { background: #FAFAFA; }
        .ps-result.ps-selected { background: rgba(46,135,61,0.08); border-left-color: #2e873d; }
        .ps-icon {
          width: 32px; height: 32px; border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          color: #484848; background: #F1F2F4; flex-shrink: 0;
        }
        .ps-result.ps-selected .ps-icon { background: #d9f4d7; color: #2e873d; }
        .ps-text { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .ps-primary {
          font-size: 13.5px; font-weight: 600; color: #232323;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .ps-primary .ps-hl {
          background: rgba(46,135,61,0.18);
          color: inherit; padding: 0 1px; border-radius: 2px;
        }
        .ps-secondary {
          font-size: 11.5px; color: #747474;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .ps-meta {
          font-size: 11px; color: #747474;
          font-variant-numeric: tabular-nums;
          display: flex; flex-direction: column; align-items: flex-end; gap: 2px;
        }
        .ps-tier {
          font-size: 9.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
          padding: 1px 6px; border-radius: 3px; line-height: 1.5;
        }
        .ps-tier-public   { color: #2563EB; background: rgba(37,99,235,0.10); }
        .ps-tier-governed { color: #2e873d; background: #d9f4d7; }
        .ps-tier-pii      { color: #B91C1C; background: rgba(185,28,28,0.10); }
        .ps-preview {
          background: #FAFAFA; padding: 22px; overflow-y: auto;
          display: flex; flex-direction: column; gap: 16px;
        }
        .ps-prev-empty {
          color: #9aa0a6; font-size: 12.5px; line-height: 1.55; text-align: center; padding: 60px 12px;
        }
        .ps-prev-eyebrow {
          font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #747474;
        }
        .ps-prev-title {
          font-size: 17px; font-weight: 700; color: #232323;
          letter-spacing: -0.01em; line-height: 1.3;
        }
        .ps-prev-sub { font-size: 12px; color: #747474; line-height: 1.5; }
        .ps-divider { height: 1px; background: #EDEDED; }
        .ps-row {
          display: grid; grid-template-columns: 110px 1fr;
          gap: 12px; align-items: baseline; font-size: 12px;
        }
        .ps-row-k { color: #747474; font-size: 11px; letter-spacing: 0.02em; }
        .ps-row-v { color: #232323; font-weight: 600; font-variant-numeric: tabular-nums; word-break: break-word; }
        .ps-actions { display: flex; gap: 8px; margin-top: 4px; }
        .ps-actions button {
          flex: 1; padding: 8px 12px; font-size: 12px; font-weight: 600;
          border-radius: 5px; border: 1px solid #E1E1E1;
          background: #FFFFFF; color: #484848; cursor: pointer;
          font-family: inherit; transition: all 0.12s;
        }
        .ps-actions button:hover { border-color: #2e873d; color: #2e873d; background: rgba(46,135,61,0.08); }
        .ps-actions button.ps-primary { background: #2e873d; color: #FFFFFF; border-color: #2e873d; }
        .ps-actions button.ps-primary:hover { background: #226a2e; border-color: #226a2e; }
        .ps-foot {
          display: flex; align-items: center; gap: 16px;
          padding: 10px 20px;
          background: #FAFAFA;
          border-top: 1px solid #EDEDED;
          font-size: 11px; color: #747474;
        }
        .ps-foot-hint { display: inline-flex; align-items: center; gap: 6px; }
        .ps-foot .ps-right { margin-left: auto; }
        .ps-recent-block { padding: 14px 20px 20px; }
        .ps-recent-block + .ps-recent-block { border-top: 1px solid #EDEDED; padding-top: 16px; }
        .ps-recent-title {
          font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
          color: #747474; margin-bottom: 10px;
        }
        .ps-chips { display: flex; gap: 6px; flex-wrap: wrap; }
        .ps-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 10px; background: #FAFAFA;
          border: 1px solid #E1E1E1; border-radius: 16px;
          font-size: 12px; color: #484848; cursor: pointer;
          font-family: inherit; transition: all 0.12s;
        }
        .ps-chip:hover { border-color: #2e873d; color: #2e873d; background: #FFFFFF; }
        .ps-no-results { padding: 60px 20px 70px; text-align: center; color: #747474; }
        .ps-no-results-title { font-size: 14px; font-weight: 600; color: #484848; margin-bottom: 4px; }
        .ps-no-results small { display: block; font-size: 12px; color: #9aa0a6; margin-top: 10px; }
        .ps-loading { display: flex; align-items: center; justify-content: center; padding: 60px; }
        .ps-error {
          margin: 14px 20px;
          padding: 10px 14px;
          font-size: 12px; color: #B91C1C;
          background: rgba(185, 28, 28, 0.06);
          border: 1px solid rgba(185, 28, 28, 0.18);
          border-radius: 4px;
        }
      `}</style>

      <div className="ps-overlay" role="dialog" aria-modal="true" aria-label="Portal search" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="ps-modal">

          {/* Input */}
          <div className="ps-input-wrap">
            <Icon iconName="Search" style={{ color: "#747474", fontSize: 16 }} />
            <input
              ref={inputRef}
              className="ps-input"
              type="text"
              placeholder="Search projects, suppliers, cost centres, customers…"
              autoComplete="off"
              spellCheck={false}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="ps-esc"><span className="ps-kbd">Esc</span></div>
          </div>

          {/* Scope tabs — "All" + each granted non-meta entity type. Clicking
              narrows search to that entity. Default scope ("All") excludes
              dashboards/datasets — those have dedicated nav surfaces. */}
          {scopeTabs.length > 0 && (
            <div className="ps-scope">
              <span className="ps-scope-label">Search in:</span>
              <button
                className={"ps-scope-tab" + (activeScope === "all" ? " ps-scope-active" : "")}
                onClick={() => setActiveScope("all")}
              >
                All
              </button>
              {scopeTabs.map((t) => (
                <button
                  key={t}
                  className={"ps-scope-tab" + (activeScope === t ? " ps-scope-active" : "")}
                  onClick={() => setActiveScope(t)}
                >
                  <Icon iconName={ENTITY_ICON[t] || "Page"} style={{ fontSize: 12 }} />
                  {entityPlural(t)}
                  {t === "customer" ? <span className="ps-scope-pii">PII</span> : null}
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          <div className="ps-body">
            <div className="ps-results">
              {!q.trim() ? (
                <EmptyState scope={scope} onPick={(query) => setQ(query)} />
              ) : loading ? (
                <div className="ps-loading"><Spinner size={SpinnerSize.medium} label="Searching…" /></div>
              ) : error ? (
                <div className="ps-error">{error}</div>
              ) : !flat.length ? (
                <NoResults query={q.trim()} />
              ) : (
                <ResultsView groups={groups} selectedIdx={selectedIdx} q={q.trim()} setSelectedIdx={setSelectedIdx} onPick={confirm} />
              )}
            </div>
            <aside className="ps-preview">
              {selected ? <Preview item={selected} onOpen={confirm} /> : <PreviewEmpty />}
            </aside>
          </div>

          {/* Footer */}
          <div className="ps-foot">
            <span className="ps-foot-hint"><span className="ps-kbd">↑</span><span className="ps-kbd">↓</span> Navigate</span>
            <span className="ps-foot-hint"><span className="ps-kbd">↵</span> Open</span>
            <span className="ps-right">Results filtered by your access</span>
          </div>
        </div>
      </div>

      {/* Entity 360 drawer — overlays the modal when a result is selected */}
      {drawerTarget && (
        <EntityDrawer
          entityType={drawerTarget.entityType}
          id={drawerTarget.id}
          fallbackTitle={drawerTarget.title}
          onClose={closeDrawer}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
function ResultsView({
  groups, selectedIdx, q, setSelectedIdx, onPick,
}: {
  groups: ResultGroup[];
  selectedIdx: number;
  q: string;
  setSelectedIdx: (i: number) => void;
  onPick: () => void;
}) {
  let globalIdx = 0;
  const out: JSX.Element[] = [];
  groups.forEach((g) => {
    out.push(
      <div key={`hdr-${g.entityType}`} className="ps-group-header">
        {g.type}
        <span className="ps-count">· {g.count}</span>
      </div>
    );
    g.items.forEach((item) => {
      const myIdx = globalIdx++;
      const isSel = myIdx === selectedIdx;
      const iconName = ENTITY_ICON[item.entityType] || "Page";
      out.push(
        <div
          key={`row-${item.entityType}-${myIdx}`}
          className={"ps-result" + (isSel ? " ps-selected" : "")}
          ref={(el) => { if (isSel && el) el.scrollIntoView({ block: "nearest" }); }}
          onMouseEnter={() => setSelectedIdx(myIdx)}
          onClick={onPick}
        >
          <div className="ps-icon"><Icon iconName={iconName} style={{ fontSize: 14 }} /></div>
          <div className="ps-text">
            <div className="ps-primary">{renderHighlighted(item.primary, q)}</div>
            <div className="ps-secondary">{item.secondary}</div>
          </div>
          <div className="ps-meta">
            <span className={"ps-tier ps-tier-" + item.tier}>
              {item.tier === "pii" ? "PII" : item.tier}
            </span>
            {item.meta ? <span>{item.meta}</span> : null}
          </div>
        </div>
      );
    });
  });
  return <>{out}</>;
}

function renderHighlighted(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <span className="ps-hl">{text.slice(i, i + q.length)}</span>
      {text.slice(i + q.length)}
    </>
  );
}

function EmptyState({ scope, onPick }: { scope: string[]; onPick: (q: string) => void }) {
  const searchable = scope.filter((s) => !META_ENTITY_TYPES.has(s));
  return (
    <>
      <div className="ps-recent-block">
        <div className="ps-recent-title">Suggested searches</div>
        <div className="ps-chips">
          {RECENT_DEFAULT.map((r) => (
            <button key={r.query} className="ps-chip" onClick={() => onPick(r.query)}>
              <Icon iconName="Clock" style={{ fontSize: 11, color: "#9aa0a6" }} />
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="ps-recent-block">
        <div className="ps-recent-title">You have access to</div>
        <div className="ps-chips">
          {searchable.length === 0 ? (
            <span style={{ fontSize: 12, color: "#9aa0a6" }}>
              No searchable entities yet — contact your administrator to request access.
            </span>
          ) : searchable.map((s) => (
            <span key={s} className="ps-chip" style={{ cursor: "default" }}>
              <Icon iconName={ENTITY_ICON[s] || "Page"} style={{ fontSize: 11, color: "#9aa0a6" }} />
              {entityPlural(s)}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}

function entityLabel(s: string): string {
  return s.split("_").map((p) => p[0].toUpperCase() + p.slice(1)).join(" ");
}

function entityPlural(s: string): string {
  // Display label for scope tabs and chips. Plurals match the result-group
  // headers the backend returns.
  const map: Record<string, string> = {
    cost_centre:     "Cost Centres",
    project:         "Projects",
    supplier:        "Suppliers",
    purchase_order:  "Purchase Orders",
    property:        "Properties",
    permit:          "Permits",
    infringement:    "Infringements",
    asset:           "Assets",
    service_request: "Service Requests",
    customer:        "Customers",
  };
  return map[s] || entityLabel(s);
}

function NoResults({ query }: { query: string }) {
  return (
    <div className="ps-no-results">
      <div className="ps-no-results-title">No matches for "{query}"</div>
      <div>Try a project number, cost-centre code, or supplier name.</div>
      <small>Results are filtered by your access. Some entities may be hidden.</small>
    </div>
  );
}

function PreviewEmpty() {
  return (
    <div className="ps-prev-empty">
      <Icon iconName="Search" style={{ fontSize: 32, marginBottom: 12, opacity: 0.6 }} />
      <div>Start typing to search across projects, suppliers, cost centres, and dashboards you have access to.</div>
    </div>
  );
}

function Preview({ item, onOpen }: { item: ResultItem; onOpen: () => void }) {
  const hasDrawer = DRAWER_ENTITY_TYPES.has(item.entityType);
  return (
    <>
      <div>
        <div className="ps-prev-eyebrow">{item.preview.eyebrow}</div>
        <div className="ps-prev-title">{item.preview.title}</div>
        {item.preview.sub ? <div className="ps-prev-sub" style={{ marginTop: 4 }}>{item.preview.sub}</div> : null}
      </div>
      <div className="ps-divider" />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {item.preview.rows.map(([k, v], i) => (
          <div key={i} className="ps-row">
            <div className="ps-row-k">{k}</div>
            <div className="ps-row-v">{v}</div>
          </div>
        ))}
      </div>
      <div className="ps-divider" />
      <div className="ps-actions">
        <button className="ps-primary" onClick={onOpen}>
          {hasDrawer ? "View full profile" : "Open"}
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Combined hook — handles Cmd/Ctrl-K listener + open state.
// Use this from TopNav so the listener is mounted once, app-wide.
// ─────────────────────────────────────────────────────────────────────────────
export function usePortalSearch() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return { open, openSearch: () => setOpen(true), closeSearch: () => setOpen(false) };
}
